import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api.js';

const STORAGE_KEY = 'ha_active_empresa';

type EmpresaItem = { id: string; name: string };

type WorkspaceContextValue = {
  empresas: EmpresaItem[];
  activeEmpresaId: string | null;
  activeEmpresa: EmpresaItem | null;
  setActiveEmpresaId: (id: string | null) => void;
  isLoading: boolean;
  refreshEmpresas: () => void;
};

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [activeEmpresaId, setActiveEmpresaIdState] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(STORAGE_KEY);
  });

  const empresasQuery = useQuery({
    queryKey: ['empresas'],
    queryFn: async () => {
      const r = await api.get<{ items: EmpresaItem[] }>('/empresas');
      return r.data.items;
    },
  });

  const empresas = empresasQuery.data ?? [];

  useEffect(() => {
    if (!empresas.length) return;
    const stored = localStorage.getItem(STORAGE_KEY);
    const valid = stored && empresas.some((e) => e.id === stored);
    if (valid) {
      if (stored !== activeEmpresaId) setActiveEmpresaIdState(stored);
      return;
    }
    const first = empresas[0]!.id;
    localStorage.setItem(STORAGE_KEY, first);
    setActiveEmpresaIdState(first);
  }, [empresas, activeEmpresaId]);

  const setActiveEmpresaId = useCallback(
    (id: string | null) => {
      if (id) localStorage.setItem(STORAGE_KEY, id);
      else localStorage.removeItem(STORAGE_KEY);
      setActiveEmpresaIdState(id);
      void qc.invalidateQueries({ queryKey: ['empresa'] });
      void qc.invalidateQueries({ queryKey: ['diagnosticos'] });
    },
    [qc],
  );

  const activeEmpresa = useMemo(
    () => empresas.find((e) => e.id === activeEmpresaId) ?? null,
    [empresas, activeEmpresaId],
  );

  const value = useMemo(
    () => ({
      empresas,
      activeEmpresaId,
      activeEmpresa,
      setActiveEmpresaId,
      isLoading: empresasQuery.isLoading,
      refreshEmpresas: () => void empresasQuery.refetch(),
    }),
    [empresas, activeEmpresaId, activeEmpresa, setActiveEmpresaId, empresasQuery.isLoading, empresasQuery],
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace(): WorkspaceContextValue {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error('useWorkspace must be used within WorkspaceProvider');
  return ctx;
}
